import { useEffect, useMemo, useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import FormButton from '../../components/formElements/FormButton';
import { SubscriptionApi } from '../../services/Subscription';

const AddSubscriptionModal = ({ dialog, setDialog, getSubscriptionList }) => {
    const [formData, setFormData] = useState({
        title: '',
        id: '',
        description: '',
        price: ''
    });

    const [errors, setErrors] = useState({
        title: '',
        description: '',
        price: ''
    });

    const closeModal = () => {
        setDialog({ ...dialog, isOpen: false, data: {} });
        setErrors({ title: '', description: '', price: '' });
        setFormData({ title: '', id: '', description: '', price: '' });
    };

    const handleSubmit = async () => {
        let hasError = false;
        let newErrors = { title: '', description: '', price: '' };

        // Title validation
        if (!formData.title.trim()) {
            newErrors.title = 'El nombre es requerido.';
            hasError = true;
        }

        // Description validation
        if (!formData.description.trim()) {
            newErrors.description = 'La descripción es requerida.';
            hasError = true;
        }

        // Price validation
        if (!formData.price) {
            newErrors.price = 'El valor es requerido.';
            hasError = true;
        } else if (isNaN(formData.price) || Number(formData.price) < 0) {
            newErrors.price = 'El valor debe ser un número positivo.';
            hasError = true;
        }

        setErrors(newErrors);

        if (hasError) return; // validation failed, do not submit

        try {
            if (!dialog?.data?.id) {
                delete formData.id;
            }
            const payload = {
                ...formData,
                price: parseInt(formData.price, 10)
            };
            if (dialog.id) {
                await SubscriptionApi.edit(payload);
            } else {
                await SubscriptionApi.add(payload);
            }
            getSubscriptionList();
        } catch (error) {
            console.log(error);
        } finally {
            closeModal();
        }
    };

    useEffect(() => {
        if (dialog?.isOpen) {
            setFormData({
                title: dialog?.data?.title || '',
                id: dialog?.data?.id || '',
                description: dialog?.data?.description || '',
                price: dialog?.data?.price || ''
            });
            setErrors({ title: '', description: '', price: '' }); // clear errors on open
        }
    }, [dialog]);

    const handleValueInput = (e) => {
        const key = e.keyCode;
        // Only allow numbers to be entered
        if ((key < 48 || key > 57) && (key < 96 || key > 105) && key !== 8) {
            e.preventDefault();
        }
    };

    return (
        <>
            <Transition appear show={dialog.isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={closeModal}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                        {dialog.id ? 'Editar suscripción' : 'Agregar suscripción'}
                                    </Dialog.Title>
                                    <div className="mt-2">
                                        <div className="mb-4 items-center grid gap-4 grid-cols-3">
                                            <label>Nombre :</label>
                                            <input
                                                className={`col-span-2 rounded border px-2 py-1 focus:outline-none ${
                                                    errors.title ? 'border-red-600' : 'border-gray-300'
                                                }`}
                                                type="text"
                                                value={formData?.title}
                                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                            />
                                            {errors.title && (
                                                <p className="col-span-3 text-red-600 text-sm mt-1">{errors.title}</p>
                                            )}
                                        </div>

                                        <div className="mb-4 items-center grid gap-4 grid-cols-3">
                                            <label>Descripción :</label>
                                            <textarea
                                                className={`col-span-2 rounded border px-2 py-1 focus:outline-none ${
                                                    errors.description ? 'border-red-600' : 'border-gray-300'
                                                }`}
                                                maxLength={100}
                                                value={formData?.description}
                                                onChange={(e) =>
                                                    setFormData({ ...formData, description: e.target.value })
                                                }
                                            />
                                            {errors.description && (
                                                <p className="col-span-3 text-red-600 text-sm mt-1">
                                                    {errors.description}
                                                </p>
                                            )}
                                        </div>

                                        <div className="mb-4 items-center grid gap-4 grid-cols-3">
                                            <label>Valor :</label>
                                            <input
                                                className={`col-span-2 rounded border px-2 py-1 focus:outline-none ${
                                                    errors.price ? 'border-red-600' : 'border-gray-300'
                                                }`}
                                                type="text"
                                                onKeyDown={handleValueInput}
                                                value={formData?.price}
                                                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                            />
                                            {errors.price && (
                                                <p className="col-span-3 text-red-600 text-sm mt-1">{errors.price}</p>
                                            )}
                                        </div>

                                        <div className="mt-4">
                                            <FormButton
                                                label={!dialog.id ? 'Agregar' : 'Actualizar'}
                                                type="button"
                                                onClick={handleSubmit}
                                            />
                                        </div>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    );
};

export default AddSubscriptionModal;
